/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * ConfirmModal – a reusable confirmation dialog that replaces window.confirm().
 *
 * Usage:
 *   <ConfirmModal
 *     open={showConfirm}
 *     onClose={() => setShowConfirm(false)}
 *     onConfirm={handleConfirmedAction}
 *     title="Close Trade"
 *     message="Are you sure you want to close this trade?"
 *     confirmLabel="Close"
 *     confirmVariant="danger"     // "danger" | "primary" (default)
 *     loading={isClosing}
 *   />
 */

import React from 'react';
import { createPortal } from 'react-dom';

export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title = 'Confirm',
  message = 'Are you sure?',
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'danger', // "danger" | "primary"
  loading = false,
  icon,
}) {
  if (!open) return null;

  const confirmClasses =
    confirmVariant === 'danger'
      ? 'bg-red-600 hover:bg-red-500 active:bg-red-700 focus:ring-red-500/40'
      : 'bg-blue-600 hover:bg-blue-500 active:bg-blue-700 focus:ring-blue-500/40';

  const defaultIcon =
    confirmVariant === 'danger' ? (
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
        </svg>
      </div>
    ) : (
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/15">
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
        </svg>
      </div>
    );

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
      />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-sm rounded-xl border border-borderDefault bg-backgroundSurface shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150">
        <div className="p-5 sm:p-6">
          {/* Icon */}
          {icon || defaultIcon}

          {/* Title */}
          <h3 className="mt-4 text-center text-lg font-semibold text-textPrimary">{title}</h3>

          {/* Message */}
          {message && (
            <p className="mt-2 text-center text-sm text-textSecondary">{message}</p>
          )}

          {/* Extra content (e.g., trade details) */}
          {children && <div className="mt-3">{children}</div>}
        </div>

        {/* Actions */}
        <div className="flex gap-3 border-t border-borderDefault bg-backgroundSurface/50 px-5 py-4 sm:px-6">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 rounded-lg border border-borderDefault px-4 py-2.5 text-sm font-medium text-textSecondary
                       hover:bg-secondary/30 active:bg-secondary/50 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-backgroundSurface
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2 ${confirmClasses}`}
          >
            {loading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            )}
            {loading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
